/**
 * 배경 전용 캔버스
 *
 * InfiniteBackground, 도로, 커서, 장식을 렌더링합니다.
 * 마우스 이벤트를 처리하고 CameraActions를 스토어에 등록합니다.
 * z-index: 0으로 가장 뒤에 렌더링됩니다.
 */
import { useRef, useCallback, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { IsometricCamera } from "@/three/cameras/IsometricCamera";
import { InfiniteBackground } from "@/three/components/Grid/InfiniteBackground";
import { RoadMeshRenderer } from "@/three/components/Road/RoadMeshRenderer";
import { RealtimeCursors } from "@/three/components/RealtimeCursors";
import { useObliqueProjection } from "@/three/hooks/useObliqueProjection";
import { useMouseControls } from "@/three/hooks/useMouseControls";
import { useGridRaycasting } from "@/three/hooks/useGridRaycasting";
import { useCameraObservableStore } from "@/stores/cameraObservableStore";
import { useMousePositionStore } from "@/stores/mousePositionStore";
import { GRID_CONFIG } from "@/three/config/grid";
import { DEFAULT_PROJECTION_PARAMS } from "@/three/config/presets";
import type { ProjectionParams } from "@/three/config/types";
import type { OtherCursor } from "@/stores/cameraStore";
import type { RoadSegment } from "@/three/utils/clusteringAlgorithm";
import type { CameraActions } from "@/three/events";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";

interface BackgroundCanvasProps {
  /** 커서 목록 */
  cursors: OtherCursor[];
  /** 내 커서 */
  myCursor: { gridX: number; gridZ: number; color?: string } | null;
  /** 다크 모드 */
  isDarkMode: boolean;
  /** 도로 세그먼트 */
  roadSegments: RoadSegment[];
  /** 에디터 모드 (도로 투명도용) */
  editorMode?: boolean;
  /** 건물 선택됨 (도로 투명도용) */
  selectedBuildingId?: string | null;
  /** 입주 슬라이더 모드 */
  tenantSliderMode?: boolean;
  // 마우스 이벤트 콜백
  onCellPointerOver?: (x: number, z: number) => void;
  onCellPointerOut?: () => void;
  onCellClick?: (onBuildingNavigate?: (id: string) => void) => void;
  onBuildingNavigate?: (id: string) => void;
  updateCursor?: (x: number, z: number) => void;
  onEdgeZoneChange?: (edgeZone: {
    left: boolean;
    right: boolean;
    top: boolean;
    bottom: boolean;
  }) => void;
  onControlsReady?: (controls: CameraActions) => void;
  isInputBlocked?: boolean;
  cursorNone?: boolean;
}

/**
 * 배경 씬 컨텐츠
 * 마우스 이벤트를 처리하고 배경을 렌더링
 */
function BackgroundSceneContent({
  cursors,
  myCursor,
  isDarkMode,
  roadSegments,
  editorMode = false,
  selectedBuildingId,
  tenantSliderMode = false,
  onCellPointerOver,
  onCellPointerOut,
  onCellClick,
  onBuildingNavigate,
  updateCursor,
  onEdgeZoneChange,
  onControlsReady,
  isInputBlocked = false,
}: BackgroundCanvasProps) {
  const sceneGroupRef = useRef<THREE.Group>(null);
  const projectionParams: ProjectionParams = DEFAULT_PROJECTION_PARAMS;
  const mousePosition = useMousePositionStore((state) => state.mousePosition);
  const setCameraActions = useCameraObservableStore(
    (state) => state.setCameraActions
  );

  // 마우스 컨트롤
  const handleLeftClick = useCallback(() => {
    if (editorMode) return;
    onCellClick?.(onBuildingNavigate);
  }, [onCellClick, onBuildingNavigate, editorMode]);

  const { getPanOffset, getEdgeZone, cameraActionsRef } = useMouseControls({
    onLeftClick: handleLeftClick,
    enabled: !isInputBlocked,
  });

  // 부모에게 컨트롤 인스턴스 전달 + 스토어에 등록
  useEffect(() => {
    if (cameraActionsRef.current) {
      onControlsReady?.(cameraActionsRef.current);
      setCameraActions(cameraActionsRef.current);
    }
    return () => {
      setCameraActions(null);
    };
  }, [cameraActionsRef, onControlsReady, setCameraActions]);

  // Oblique 투영 적용
  useObliqueProjection(sceneGroupRef, projectionParams, getPanOffset);

  // 그리드 레이캐스팅
  useGridRaycasting({
    mousePosition,
    projectionParams,
    getPanOffset,
    onCellPointerOver: (x, z) => {
      if (editorMode) return;
      onCellPointerOver?.(x, z);
      updateCursor?.(
        x + GRID_CONFIG.CELL_SIZE / 2,
        z + GRID_CONFIG.CELL_SIZE / 2
      );
    },
    onCellPointerOut: () => {
      if (editorMode) return;
      onCellPointerOut?.();
    },
  });

  // 엣지 존 감지
  const lastEdgeZoneRef = useRef<{
    left: boolean;
    right: boolean;
    top: boolean;
    bottom: boolean;
  }>({ left: false, right: false, top: false, bottom: false });

  useFrame(() => {
    const edgeZone = tenantSliderMode
      ? { left: false, right: false, top: false, bottom: false }
      : getEdgeZone();
    const lastZone = lastEdgeZoneRef.current;

    if (
      edgeZone.left !== lastZone.left ||
      edgeZone.right !== lastZone.right ||
      edgeZone.top !== lastZone.top ||
      edgeZone.bottom !== lastZone.bottom
    ) {
      lastEdgeZoneRef.current = edgeZone;
      onEdgeZoneChange?.(edgeZone);
    }
  });

  // 도로 투명도 계산
  const roadOpacity = editorMode || selectedBuildingId ? 0.1 : 1.0;

  return (
    <>
      {/* 카메라 */}
      <IsometricCamera position={[30, 30, 30]} zoom={40} />

      {/* 무한 배경 (Oblique 역행렬 적용) */}
      <InfiniteBackground
        projectionParams={projectionParams}
        getPanOffset={getPanOffset}
        isDarkMode={isDarkMode}
      />

      {/* Oblique 투영이 적용되는 그룹 */}
      <group ref={sceneGroupRef}>
        {/* 도로 */}
        <RoadMeshRenderer
          roadSegments={roadSegments}
          maxSegments={1500}
          opacity={roadOpacity}
        />

        {/* 커서 (에디터/입주 모드가 아닐 때만) */}
        {!editorMode && !tenantSliderMode && (
          <RealtimeCursors cursors={cursors} myCursor={myCursor} />
        )}
      </group>
    </>
  );
}

/**
 * 배경 캔버스 컴포넌트
 */
export function BackgroundCanvas(props: BackgroundCanvasProps) {
  return (
    <Canvas
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        // 마우스 이벤트 활성화
        cursor: props.cursorNone ? "none" : "auto",
      }}
      gl={{
        antialias: true,
        alpha: false, // 배경은 불투명
      }}

    >
      <QueryClientProvider client={queryClient}>
        <BackgroundSceneContent {...props} />
      </QueryClientProvider>
    </Canvas>
  );
}
